import {
  AuditDetail,
  AuditList,
  NavCounts,
  type AuditCategory,
  TrashList,
  type TrashType,
} from '@deizi/shared';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export const trashKey = ['trash'] as const;
export const auditKey = ['audit'] as const;

export const useTrash = () =>
  useQuery({ queryKey: trashKey, queryFn: () => api.get('/trash', TrashList) });

export interface AuditFilters {
  category: AuditCategory | null;
  actorUserId: string;
  days: number | null;
}

export function auditParams(f: AuditFilters, isoToday: string): URLSearchParams {
  const p = new URLSearchParams();
  if (f.category) p.set('category', f.category);
  if (f.actorUserId) p.set('actorUserId', f.actorUserId);
  if (f.days) {
    const d = new Date(`${isoToday}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - f.days + 1);
    p.set('from', d.toISOString().slice(0, 10));
  }
  return p;
}

export function useAudit(params: URLSearchParams) {
  return useInfiniteQuery({
    queryKey: [...auditKey, params.toString()],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(params);
      p.set('limit', '50');
      if (pageParam) p.set('cursor', pageParam);
      return api.get(`/audit?${p.toString()}`, AuditList);
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export const useAuditEntry = (id: string | null) =>
  useQuery({
    queryKey: [...auditKey, 'entry', id],
    queryFn: () => api.get(`/audit/${id}`, AuditDetail),
    enabled: !!id,
  });

/** Restores and deletions change almost every list. */
export function useInvalidateAll() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries();
}

export const trashPath = (type: TrashType, id: string) => `/trash/${type}/${id}`;

/** The small numbers in the menu (Orçamentos, Pedidos, Filamentos), refreshed every minute. */
export const useNavCounts = () =>
  useQuery({
    queryKey: ['nav-counts'],
    queryFn: () => api.get('/nav-counts', NavCounts),
    refetchInterval: 60_000,
  });

/** Count shown next to a menu item, by its key in nav.ts. */
export function navCount(counts: NavCounts | undefined, key: string): number {
  if (!counts) return 0;
  if (key === 'orcamentos') return counts.quotes;
  if (key === 'pedidos') return counts.orders;
  if (key === 'filamentos') return counts.filaments;
  return 0;
}
